import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Package, CalendarDays } from "lucide-react";
import { getPharmacyOrders } from "../../../services/pharmacy/pharmacyOrderService";

import "./PharmacyOrders.css";

const STATUS_LABELS = {
    0: "در انتظار بررسی",
    1: "در انتظار پرداخت",
    2: "آماده سازی",
    3: "در حال ارسال",
    4: "تحویل سفارش",
    6: "لغو شده",
};

export default function PharmacyOrders() {
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("active");
    const [orders, setOrders] = useState([]);
    const [counts, setCounts] = useState({
        active: 0,
        delivered: 0,
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        loadOrders();
    }, [activeTab]);

    const loadOrders = async () => {
        try {
            setLoading(true);
            setError("");

            const result = await getPharmacyOrders(activeTab);

            if (!result?.success) {
                throw new Error("امکان دریافت سفارش‌ها وجود ندارد.");
            }

            // Laravel paginate() returns the records inside orders.data
            setOrders(result.orders?.data || []);

            setCounts({
                active: Number(result.counts?.active || 0),
                delivered: Number(result.counts?.delivered || 0),
            });

        } catch (err) {
            console.error("Load pharmacy orders:", err);

            setError(
                err.response?.data?.message ||
                err.message ||
                "خطا در دریافت سفارش‌ها."
            );

        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return "-";

        return new Date(date).toLocaleDateString("fa-IR", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const formatPrice = (price) => {
        return Number(price || 0).toLocaleString("fa-IR");
    };

    const openOrderDetails = (orderId) => {
        navigate(`/dashboard/pharmacy/orders/${orderId}`);
    };

    return (
        <section className="pharmacy-orders-section" dir="rtl">

            {/* Section heading */}
            <div className="pharmacy-orders-heading">
                <h3>سفارشات من</h3>
            </div>

            {/* Order tabs */}
            <div className="pharmacy-orders-tabs">

                <button
                    type="button"
                    className={
                        activeTab === "active"
                            ? "pharmacy-orders-tab active"
                            : "pharmacy-orders-tab"
                    }
                    onClick={() => setActiveTab("active")}
                >
                    سفارش‌های فعال
                    <span>({counts.active.toLocaleString("fa-IR")})</span>
                </button>

                <button
                    type="button"
                    className={
                        activeTab === "delivered"
                            ? "pharmacy-orders-tab active"
                            : "pharmacy-orders-tab"
                    }
                    onClick={() => setActiveTab("delivered")}
                >
                    سفارش‌های تحویل شده
                    <span>({counts.delivered.toLocaleString("fa-IR")})</span>
                </button>

            </div>

            {/* Orders list */}
            <div className="pharmacy-orders-list">

                {loading ? (
                    <div className="pharmacy-orders-state">
                        <span className="prescription-spinner" />
                        <p>در حال دریافت سفارش‌ها...</p>
                    </div>
                ) : error ? (
                    <div className="pharmacy-orders-state pharmacy-orders-error">
                        <p>{error}</p>

                        <button type="button" onClick={loadOrders}>
                            تلاش مجدد
                        </button>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="pharmacy-orders-empty">
                        <Package size={38} />
                        <p>
                            {activeTab === "active"
                                ? "سفارش فعالی ندارید."
                                : "هنوز سفارشی تحویل نگرفته‌اید."}
                        </p>
                    </div>
                ) : (
                    orders.map((order) => (
                        <article
                            className="pharmacy-order-card"
                            key={order.id}
                        >

                            <div className="pharmacy-order-card-main">

                                <div className="pharmacy-order-card-info">

                                    <strong className="pharmacy-order-status">
                                        {STATUS_LABELS[Number(order.status)] ||
                                            "نامشخص"}
                                    </strong>

                                    <span className="pharmacy-order-code">
                                        سفارش
                                        <b>{order.id}</b>
                                        {" "}نسخه دارو
                                    </span>

                                </div>

                                <button
                                    type="button"
                                    className="pharmacy-order-details-link"
                                    onClick={() => openOrderDetails(order.id)}
                                >
                                    جزئیات سفارش
                                    <ChevronLeft size={18} />
                                </button>

                            </div>

                            <div className="pharmacy-order-card-footer">

                                <span className="pharmacy-order-date">
                                    <CalendarDays size={15} />
                                    {formatDate(order.created_at)}
                                </span>

                                {Number(order.amount) > 0 && (
                                    <span className="pharmacy-order-price">
                                        {formatPrice(order.amount)} تومان
                                    </span>
                                )}

                            </div>

                        </article>
                    ))
                )}

            </div>

        </section>
    );
}